import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  SegmentedControl,
} from "@mantine/core";
import { IconMail, IconMapPin } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { PaginatedList } from "@components/shared";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "gray";
  if (status === 3) return "red";
  return "gray";
}

function locationLabel({ city, state_province, country }) {
  return [city, state_province, country].filter(Boolean).join(", ");
}

export default function OrganizationList({
  items = [],
  requestInfo = {
    url: `/retrieve-organizations/`,
    data: {},
  },
  refresh,
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    scope: "active",
    search_value: "",
  });

  const reqData = {
    ...filters,
    ...requestInfo.data,
    refresh,
  };

  return (
    <div>
      {requestInfo && (
        <Stack gap="sm" mb="sm">
          <TextInput
            placeholder={t("search")}
            size="lg"
            value={filters.search_value}
            onChange={(e) =>
              setFilters({ ...filters, search_value: e.target.value })
            }
          />
          <SegmentedControl
            data={[
              { label: t("all"), value: "entire" },
              { label: t("active"), value: "active" },
              { label: t("inactive"), value: "inactive" },
              { label: t("deleted"), value: "deleted" },
            ]}
            value={filters.scope}
            onChange={(value) => setFilters({ ...filters, scope: value })}
            size="xs"
          />
        </Stack>
      )}
      <PaginatedList
        tableData={items}
        requestInfo={{
          url: requestInfo.url,
          data: reqData,
        }}
        responseMapping={(r) =>
          r.map((m) => ({
            ...m,
            link_url: `/organizations/${m.id}`,
          }))
        }
        displayFormatter={(item) => {
          const org = item.original;
          const location = locationLabel(org);

          return (
            <Card
              key={org.id}
              component={Link}
              to={`/organizations/${org.id}`}
              style={{ textDecoration: "none" }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Text fw={700} c="dark" truncate>
                    {org.name}
                  </Text>
                  {org.email && (
                    <Group gap={6} wrap="nowrap">
                      <IconMail size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="sm" c="dimmed" truncate>
                        {org.email}
                      </Text>
                    </Group>
                  )}
                  {location && (
                    <Group gap={6} wrap="nowrap">
                      <IconMapPin
                        size={14}
                        color="var(--mantine-color-dimmed)"
                      />
                      <Text size="sm" c="dimmed" truncate>
                        {location}
                      </Text>
                    </Group>
                  )}
                </Stack>
                {org.status_formatted && (
                  <Badge
                    size="sm"
                    variant="light"
                    color={statusColor(org.status)}
                    style={{ flexShrink: 0 }}
                  >
                    {org.status_formatted}
                  </Badge>
                )}
              </Group>
            </Card>
          );
        }}
      />
    </div>
  );
}
